import React, { Fragment, useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MaterialImportDeleteForm from "./MaterialImportDeleteForm";
import MaterialImportUpdateForm from "./MaterialImportUpdateForm";
import { getMaterialImports } from "../../actions/materialImports";
import MaterialImportCreateForm from "./MaterialImportCreateForm";
import MaterialImportItem from "./MaterialImportItem";
import { Plus, Filter } from "react-feather";
import TableSpinner from "../layout/TableSpinner";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import Select from 'react-select';
import { usePermission } from '../../hooks/usePermission';
import { getMaterialOptions } from '../../actions/material';
import { getSupplierOptions } from '../../actions/supplier';
import FilterByDateRange from "../layout/FilterByDateRange";
import FilterWithTrashed from "../filters/FilterWithTrashed";
import FilterOnlyTrashed from "../filters/FilterOnlyTrashed";
import { Button, Modal } from 'react-bootstrap';
import SearchForm from "../layout/SearchForm";
import useActiveFirm from '../../hooks/useActiveFirm';
import '../../styles/materialImports.css';

// Create a DeletedMaterialImportsFilterModal component similar to DeletedSalesFilterModal
const DeletedMaterialImportsFilterModal = ({ show, handleClose, getPageItems }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan material importlar filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan material importlar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha importlarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <FilterOnlyTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan importlarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const MaterialImports = () => {
    const dispatch = useDispatch();
    const { userCan } = usePermission();
    const { firmId, firmName } = useActiveFirm();
    const { materialImport } = useSelector(state => state);
    const { supplier } = useSelector(state => state);
    const { material } = useSelector(state => state);
    const { 
        materialImports, 
        loading, 
        numberOfPages, 
        currentPage,
        total_material_imports
    } = materialImport;
    const { suppliers_DATA } = supplier;
    const { material_options } = material;

    const [showCreate, setShowCreate] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const [isLoading, setIsLoading] = useState(loading ?? false);
    
    // Ref to track last fetched firmId to prevent duplicate fetches
    const lastFetchedFirmIdRef = useRef(null);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    const handleCreateClose = () => setShowCreate(false);
    const handleCreateShow = () => setShowCreate(true);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);
    
    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);

    // Helper function to get filter parameters
    const getFilterParams = () => {
        const params = new URLSearchParams(window.location.search);
        const filters = {};
        
        // Only include valid URL params (exclude undefined, null, empty strings)
        for (const [key, value] of params.entries()) {
            if (value && value !== 'undefined' && value !== 'null' && value.trim() !== '') {
                filters[key] = value;
            }
        }
        
        // Add filter values from component state
        if (selectedSupplier) filters.supplier_id = selectedSupplier.value;
        if (firmId) filters.firm_id = firmId;
        if (selectedMaterial) filters.material_id = selectedMaterial.value;
        
        // Remove any undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        
        return filters;
    };

    // Wrapper function to ensure firm_id is always included when calling getMaterialImports
    // Returns a thunk function (async action) that can be dispatched
    const getMaterialImportsWithFirm = useCallback((page, params = {}) => {
        const filters = { ...params };
        if (firmId) {
            filters.firm_id = firmId;
        }
        // Clean up undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === '' || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        // Return the thunk function, don't dispatch it
        return getMaterialImports(page, filters);
    }, [firmId]);

    // Ref to track if supplier options have been fetched
    const hasFetchedSuppliersRef = useRef(false);
    
    useEffect(() => {
        if (!hasFetchedSuppliersRef.current) {
            hasFetchedSuppliersRef.current = true;
            dispatch(getSupplierOptions());
        }
    }, [dispatch]);

    // Fetch material options when firm changes - always refetch when firmId changes
    useEffect(() => {
        if (!firmId) {
            // Don't fetch if no firm is selected
            return;
        }
        
        // Always fetch materials for the current firm
        // This ensures materials are available for the filter dropdown
        dispatch(getMaterialOptions({ firm_id: firmId }));
    }, [firmId, dispatch]);

    // Fetch material imports - only when firmId is available and hasn't been fetched for this firmId
    useEffect(() => {
        if (!firmId || lastFetchedFirmIdRef.current === firmId) return; // Don't fetch if no firm or already fetched for this firmId
        
        lastFetchedFirmIdRef.current = firmId;
        const filters = getFilterParams();
        filters.per_page = 10;
        dispatch(getMaterialImportsWithFirm(1, filters));
    }, [firmId, dispatch, getMaterialImportsWithFirm]); // Only depend on firmId - other filters handled by their own handlers

    if (!userCan('view material imports')) {
        return <div>Siz material importlarni ko'rish uchun ruxsatga ega emassiz.</div>;
    }

    const supplierOptions = suppliers_DATA ? suppliers_DATA.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) : [];

    const materialOptions = (Array.isArray(material_options) && material_options.length > 0) 
        ? material_options.map(material => ({
            value: material.id,
            label: material.name
        })) 
        : [];

    const handleSupplierChange = (selected) => {
        setSelectedSupplier(selected);
        const filters = getFilterParams();
        if (selected) {
            filters.supplier_id = selected.value;
        } else {
            delete filters.supplier_id;
        }
        filters.per_page = 10;
        dispatch(getMaterialImportsWithFirm(1, filters));
    };


    const handleMaterialChange = (selected) => {
        setSelectedMaterial(selected);
        const filters = getFilterParams();
        if (selected) {
            filters.material_id = selected.value;
        } else {
            delete filters.material_id;
        }
        filters.per_page = 10;
        dispatch(getMaterialImportsWithFirm(1, filters));
    };

    // Check if we have pagination data and total items
    const hasPaginationData = numberOfPages !== undefined && currentPage !== undefined;
    const hasItems = materialImports && materialImports.length > 0;
    const shouldShowPagination = hasPaginationData && (numberOfPages > 1 || total_material_imports > 10);

    return (
        <Fragment>
            <div className="container-fluid p-0">
                <div className="row mb-2">
                    <div className="col-md-9">
                        <h1 className="h3 mb-3">
                            <strong>Material importlari</strong> {total_material_imports ? `(${total_material_imports})` : ''}
                            {firmName && <span className="text-muted ms-2" style={{fontSize: '14px', fontWeight: 'normal'}}>— {firmName}</span>}
                        </h1>
                        <div className="alert alert-info mb-3">
                            <small>
                                <strong>Eslatma:</strong> Faqat so'nggi 2 kun ichida yaratilgan material importlarni tahrirlash va o'chirish mumkin.
                                Eski importlarni faqat direktor tahrirlashi mumkin.
                            </small>
                        </div>
                    </div>
                    <div className="col-md-3 justify-content-end">
                        <div className="crud_actions text-end d-flex justify-content-end gap-2">
                            <button 
                                className={`btn ${hasDeletedFilters ? 'btn-warning' : 'btn-outline-secondary'}`} 
                                onClick={handleDeletedFiltersShow}
                            >
                                <Filter size={16} className="me-1" />
                                {hasDeletedFilters ? 'O\'chirilgan filtr faol' : 'O\'chirilgan filtr'}
                            </button>
                            
                            {userCan('create material imports') && (
                                <button className="btn btn-primary" onClick={handleCreateShow}>
                                    Import qo'shish <Plus size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4 mb-0">
                        <Select
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            options={supplierOptions}
                            isClearable
                            placeholder="Ta'minotchini tanlang..."
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className="col-md-4">
                        <SearchForm getPageItems={getMaterialImportsWithFirm} />
                    </div>
                    <div className="col-md-4">
                        <FilterByDateRange getPageItems={getMaterialImportsWithFirm} />
                    </div>
                    <div className="col-md-4 mt-2">
                        <Select
                            value={selectedMaterial}
                            onChange={handleMaterialChange}
                            options={materialOptions}
                            isClearable
                            placeholder="Materialni tanlang..."
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                <div className="row mt-4">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <div className="card w-100 table-responsive">
                            
                            {loading && <TableSpinner/>}
                            <table className="table custom-table table-hover">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Referens</th>
                                    <th scope="col">Ta'minotchi</th>
                                    <th scope="col">Ombor/Firma</th>
                                    <th scope="col">Materiallar</th>
                                    <th scope="col">Jami summa</th>
                                    <th scope="col">Sana</th>
                                    <th scope="col">Yaratilgan sana</th>
                                    <th scope="col">Yaratuvchi</th>
                                    <th scope="col">Maxsus sotuv uchunmi?</th>
                                    <th scope="col">Amallar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {materialImports?.map((materialImport, index) => (
                                    <MaterialImportItem
                                        key={materialImport.id}
                                        materialImport={materialImport}
                                        number={index + 1}
                                    />
                                ))}
                                </tbody>
                            </table>
                            
                            {/* Show message when no items found */}
                            {!loading && (!hasItems) && (
                                <div className="text-center p-3">
                                    <p>Material importlar topilmadi</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12 col-xxl-12 d-flex justify-content-center">
                        {shouldShowPagination && (
                            <CustomPaginationWithManyPages
                                numberOfPages={numberOfPages}
                                getPageItems={getMaterialImportsWithFirm}
                                current_page={currentPage}
                                custom_params={{ per_page: 10 }}
                            />
                        )}
                    </div>
                </div>

            </div>

            <MaterialImportCreateForm show={showCreate} handleClose={handleCreateClose} />
            <MaterialImportUpdateForm />
            <MaterialImportDeleteForm />
            <DeletedMaterialImportsFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getMaterialImportsWithFirm}
            />
        </Fragment>
    );
};

export default MaterialImports;
